import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Receipt as ReceiptIcon, Download, Eye, DollarSign, CreditCard, Info, Search } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { AdminTabs } from "../../components/admin/AdminTabs";
import { downloadCsv } from "../../lib/csv";
import { printReceipt, normalizeReceipt } from "../../lib/receipt";

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div className="bg-white rounded-2xl border border-stone-border shadow-sm p-5" data-testid={testid}>
    <div className="w-10 h-10 rounded-xl bg-housing/15 flex items-center justify-center mb-3"><Icon className="w-5 h-5 text-housing" /></div>
    <div className="font-heading text-3xl font-bold text-stone-text">{value}</div>
    <div className="text-sm text-stone-muted">{label}</div>
  </div>
);

export default function AdminReceipts() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("en") ? "en" : "es";
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deployed, setDeployed] = useState(true);
  const [q, setQ] = useState("");
  const [range, setRange] = useState("all");

  useEffect(() => {
    api.get("/admin/transactions")
      .then(({ data }) => setTxs(Array.isArray(data) ? data : []))
      .catch((err) => { if (err?.response?.status === 404) setDeployed(false); })
      .finally(() => setLoading(false));
  }, []);

  const inRange = (dateStr) => {
    if (range === "all") return true;
    if (!dateStr) return false;
    const d = new Date(dateStr);
    const now = new Date();
    if (range === "today") return d.toDateString() === now.toDateString();
    const days = range === "week" ? 7 : 30;
    return d >= new Date(now.getTime() - days * 86400000);
  };

  const paid = useMemo(() => txs.filter((x) => x.payment_status === "paid" && inRange(x.created_at)), [txs, range]); // eslint-disable-line react-hooks/exhaustive-deps
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    const rows = s ? paid.filter((x) => `${x.user_name || ""} ${x.user_email || ""}`.toLowerCase().includes(s)) : paid;
    return [...rows].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  }, [paid, q]);

  const total = useMemo(() => filtered.reduce((sum, x) => sum + Number(x.amount || 0), 0), [filtered]);
  const avg = filtered.length ? total / filtered.length : 0;
  const byUser = useMemo(() => {
    const m = {};
    filtered.forEach((x) => {
      const key = x.user_email || x.user_id || "—";
      if (!m[key]) m[key] = { name: x.user_name || key, email: x.user_email || "", total: 0, count: 0 };
      m[key].total += Number(x.amount || 0);
      m[key].count += 1;
    });
    return Object.values(m).sort((a, b) => b.total - a.total).slice(0, 8);
  }, [filtered]);
  const money = (v) => `$${Number(v || 0).toFixed(2)}`;

  const RANGES = [
    { key: "all", label: t("admin.receipts.filterAll") },
    { key: "today", label: t("admin.receipts.filterToday") },
    { key: "week", label: t("admin.receipts.filterWeek") },
    { key: "month", label: t("admin.receipts.filterMonth") },
  ];

  const exportCsv = () => {
    const rows = filtered.map((tx) => {
      const r = normalizeReceipt(tx, {});
      return {
        receipt: r.number, date: r.date.toISOString().slice(0, 10),
        user: tx.user_name || "", email: tx.user_email || "",
        plan: r.plan, amount: r.amount.toFixed(2), currency: r.currency, status: r.status,
      };
    });
    downloadCsv(`restaurantejobs-receipts-${new Date().toISOString().slice(0, 10)}.csv`, rows);
    toast.success(t("admin.receipts.exportCsv") + " ✓");
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
        <h1 className="font-heading text-3xl font-bold text-stone-text">{t("admin.receipts.title")}</h1>
        {filtered.length > 0 && (
          <button data-testid="receipts-export-csv" onClick={exportCsv} className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-stone-border font-semibold text-stone-sub hover:border-brand hover:text-brand transition-colors">
            <Download className="w-4 h-4" /> {t("admin.receipts.exportCsv")}
          </button>
        )}
      </div>
      <p className="text-stone-sub mb-6">{t("admin.receipts.subtitle")}</p>
      <AdminTabs />

      {!deployed && (
        <div className="flex items-start gap-3 bg-brand-soft border border-stone-border rounded-2xl p-4 mb-6" data-testid="receipts-not-deployed">
          <Info className="w-5 h-5 text-brand shrink-0 mt-0.5" />
          <p className="text-sm text-stone-sub">{t("admin.receipts.notDeployed")}</p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <StatCard icon={DollarSign} label={t("admin.receipts.total")} value={money(total)} testid="receipts-total" />
        <StatCard icon={CreditCard} label={t("admin.receipts.count")} value={filtered.length} testid="receipts-count" />
        <StatCard icon={DollarSign} label={t("admin.receipts.avg")} value={money(avg)} testid="receipts-avg" />
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex gap-1 bg-white border border-stone-border rounded-xl p-1" data-testid="receipts-range">
          {RANGES.map((r) => (
            <button
              key={r.key}
              data-testid={`receipts-range-${r.key}`}
              onClick={() => setRange(r.key)}
              className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${range === r.key ? "bg-brand text-white" : "text-stone-sub hover:text-brand"}`}
            >
              {r.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[180px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-muted" />
          <input data-testid="receipts-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("admin.receipts.search")} className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-white border border-stone-border focus:outline-none focus:ring-2 focus:ring-brand" />
        </div>
      </div>

      {/* Total por empleador */}
      <div className="bg-white rounded-2xl border border-stone-border shadow-sm p-5 mb-6" data-testid="receipts-by-user">
        <h2 className="font-heading text-lg font-bold text-stone-text mb-3">{t("admin.receipts.byUser")}</h2>
        {byUser.length === 0 ? (
          <p className="text-sm text-stone-muted">{t("admin.receipts.byUserEmpty")}</p>
        ) : (
          <div className="space-y-2">
            {byUser.map((u, i) => (
              <div key={i} className="flex items-center justify-between gap-3 py-1.5 border-b border-stone-border/50 last:border-0" data-testid="receipts-by-user-row">
                <div className="min-w-0">
                  <div className="font-medium text-stone-text truncate">{u.name}</div>
                  <div className="text-xs text-stone-muted truncate">{u.email}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-heading font-bold text-stone-text">{money(u.total)}</div>
                  <div className="text-xs text-stone-muted">{u.count} {t("admin.receipts.count").toLowerCase()}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {loading ? (
        <div className="py-12 text-center text-stone-muted">{t("jobs.loading")}</div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center text-stone-muted bg-white rounded-2xl border border-stone-border" data-testid="receipts-empty">
          <ReceiptIcon className="w-10 h-10 mx-auto mb-3 text-stone-muted/60" />
          {t("admin.receipts.empty")}
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-stone-border shadow-sm overflow-x-auto" data-testid="receipts-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-stone-muted border-b border-stone-border">
                <th className="px-4 py-3 font-semibold">{t("admin.receipts.receipt")}</th>
                <th className="px-4 py-3 font-semibold">{t("admin.receipts.date")}</th>
                <th className="px-4 py-3 font-semibold">{t("admin.receipts.user")}</th>
                <th className="px-4 py-3 font-semibold">{t("admin.receipts.plan")}</th>
                <th className="px-4 py-3 font-semibold text-right">{t("admin.receipts.amount")}</th>
                <th className="px-4 py-3 font-semibold text-right"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((tx, i) => {
                const r = normalizeReceipt(tx, {});
                return (
                  <tr key={tx.session_id || tx.id || i} className="border-b border-stone-border/60 last:border-0 hover:bg-stone-bg/50" data-testid="receipts-row">
                    <td className="px-4 py-3 font-mono text-xs text-stone-sub">{r.number}</td>
                    <td className="px-4 py-3 text-stone-sub">{r.date.toLocaleDateString(lang === "en" ? "en-US" : "es-MX")}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-stone-text">{tx.user_name || "—"}</div>
                      <div className="text-xs text-stone-muted">{tx.user_email || ""}</div>
                    </td>
                    <td className="px-4 py-3 capitalize text-stone-sub">{r.plan}</td>
                    <td className="px-4 py-3 text-right font-semibold text-stone-text">{money(r.amount)}</td>
                    <td className="px-4 py-3 text-right">
                      <button data-testid="receipts-view" onClick={() => printReceipt(tx, { name: tx.user_name, email: tx.user_email }, lang)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-stone-border text-stone-sub hover:text-brand hover:border-brand transition-colors">
                        <Eye className="w-3.5 h-3.5" /> {t("admin.receipts.view")}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
